import { formatPrice } from "@luce/core";

import {
  PackageDepartmentEnum,
  PackageDetailSessionEnum,
  PackageServiceTypeEnum,
  PackageUnitTypeEnum,
} from "../../__generated__/graphql";
import { getFrequencyLabel } from "./lib";
import type { LineItemDetail, PackageLineItem } from "./types";

type PackageDetail = PackageLineItem["packageDetail"];

const SERVICE_TYPE_LABELS: Record<PackageServiceTypeEnum, string> = {
  [PackageServiceTypeEnum.AdHoc]: "AdHoc",
  [PackageServiceTypeEnum.Aircon]: "Aircon",
  [PackageServiceTypeEnum.ChemicalOverhaul]: "Chemical Overhaul",
  [PackageServiceTypeEnum.ChemicalWash]: "Chemical Wash",
  [PackageServiceTypeEnum.Dismantling]: "Dismantling",
  [PackageServiceTypeEnum.Equipment]: "Equipment",
  [PackageServiceTypeEnum.Formaldehyde]: "Formaldehyde Removal",
  [PackageServiceTypeEnum.GasLeakTesting]: "Gas Leak Testing",
  [PackageServiceTypeEnum.GasTopUp]: "Gas Top Up",
  [PackageServiceTypeEnum.GeneralServicing]: "General Servicing",
  [PackageServiceTypeEnum.HomeCleaning]: "Home Cleaning",
  [PackageServiceTypeEnum.Installation]: "Installation",
  [PackageServiceTypeEnum.JetWash]: "Jet Wash",
  [PackageServiceTypeEnum.OfficeCleaning]: "Office Cleaning",
  [PackageServiceTypeEnum.RepairDiagnostic]: "Repair Diagnostic",
  [PackageServiceTypeEnum.RepairFollowUp]: "Repair Follow Up",
  [PackageServiceTypeEnum.CondenserWash]: "Compressor Wash",
  [PackageServiceTypeEnum.Adjustment]: "Adjustment",
  [PackageServiceTypeEnum.Warranty]: "Warranty",
  [PackageServiceTypeEnum.CarpetUpholstery]: "Carpet Upholstery",
  [PackageServiceTypeEnum.DeepExtraction]: "Deep Extraction",
  [PackageServiceTypeEnum.Discount]: "Discount",
  [PackageServiceTypeEnum.UltraVioletCleaning]: "UV-C Cleaning",
  [PackageServiceTypeEnum.AddOn]: "Add on",
  [PackageServiceTypeEnum.Combo]: "Manicure Pedicure",
  [PackageServiceTypeEnum.Manicure]: "Manicure",
  [PackageServiceTypeEnum.Pedicure]: "Pedicure",
  [PackageServiceTypeEnum.Upgrade]: "Upgrade",
  [PackageServiceTypeEnum.Hardware]: "Hardware",
  [PackageServiceTypeEnum.SteamCleaning]: "Curtain Cleaning",
  [PackageServiceTypeEnum.DeepCleaning]: "Deep Cleaning",
  [PackageServiceTypeEnum.Electrician]: "Electrician",
  [PackageServiceTypeEnum.Lashes]: "Lashes",
  [PackageServiceTypeEnum.Mpv]: "MPV",
  [PackageServiceTypeEnum.Plumbing]: "Plumbing",
  [PackageServiceTypeEnum.Fee]: "Fee",
  [PackageServiceTypeEnum.Massage]: "Massage",
  [PackageServiceTypeEnum.Sedan]: "Sedan",
  [PackageServiceTypeEnum.Suv]: "SUV",
  [PackageServiceTypeEnum.Babysitting]: "Babysitting",
  [PackageServiceTypeEnum.PetCare]: "Pet Care",
  [PackageServiceTypeEnum.ElderCare]: "Elder Care",
  [PackageServiceTypeEnum.BodyMassage]: "Body Massage",
  [PackageServiceTypeEnum.FootMassage]: "Foot Massage",
  [PackageServiceTypeEnum.GeneralPest]: "General Pest",
  [PackageServiceTypeEnum.Termite]: "Termite",
  [PackageServiceTypeEnum.FloorServices]: "Floor Services",
  [PackageServiceTypeEnum.SecurityOfficer]: "Security Officer",
  [PackageServiceTypeEnum.SecuritySupervisor]: "Security Supervisor",
  [PackageServiceTypeEnum.SeniorSecurityOfficer]: "Senior Security Officer",
  [PackageServiceTypeEnum.DogWalking]: "Dog Walking",
  [PackageServiceTypeEnum.PetSitting]: "Pet Sitting",
  [PackageServiceTypeEnum.Transport]: "Transport",
};

const UNIT_TYPE_LABELS: Record<PackageUnitTypeEnum, string> = {
  [PackageUnitTypeEnum.All]: "All Units",
  [PackageUnitTypeEnum.R22]: "R22",
  [PackageUnitTypeEnum.R410A]: "R401",
  [PackageUnitTypeEnum.Ducted]: "Ducted",
  [PackageUnitTypeEnum.Ceiling]: "Cassette (Ceiling)",
  [PackageUnitTypeEnum.Portable]: "Portable",
  [PackageUnitTypeEnum.Wall]: "Wall-Mounted",
  [PackageUnitTypeEnum.Window]: "Window",
  [PackageUnitTypeEnum.Carpet]: "Carpet",
  [PackageUnitTypeEnum.Mattress]: "Mattress",
  [PackageUnitTypeEnum.Rug]: "Rug",
  [PackageUnitTypeEnum.Sofa]: "Sofa",
  [PackageUnitTypeEnum.Nail]: "Nail art",
  [PackageUnitTypeEnum.BabyPram]: "Baby Pram",
  [PackageUnitTypeEnum.ChildSeat]: "Child Seat",
  [PackageUnitTypeEnum.Curtain]: "Curtain",
  [PackageUnitTypeEnum.BodyMassage]: "Body Massage",
  [PackageUnitTypeEnum.Electrician]: "Electrician",
  [PackageUnitTypeEnum.FootMassage]: "Foot Massage",
  [PackageUnitTypeEnum.Handyman]: "Handyman",
  [PackageUnitTypeEnum.Installation]: "Installation",
  [PackageUnitTypeEnum.Lashes]: "Lashes",
  [PackageUnitTypeEnum.Mpv]: "MPV",
  [PackageUnitTypeEnum.Plumbing]: "Plumbing",
  [PackageUnitTypeEnum.Sedan]: "Sedan",
  [PackageUnitTypeEnum.Suv]: "SUV",
  [PackageUnitTypeEnum.Cat]: "Cat",
  [PackageUnitTypeEnum.Dog]: "Dog",
  [PackageUnitTypeEnum.Pillow]: "Pillow",
  [PackageUnitTypeEnum.HybridLashes]: "Hybrid Lash Extensions",
  [PackageUnitTypeEnum.VolumeLashes]: "Volume Lash Extensions",
  [PackageUnitTypeEnum.LiftLashes]: "Lash Lift",
  [PackageUnitTypeEnum.Chair]: "Chair",
};

const SESSION_LABELS: Record<PackageDetailSessionEnum, string> = {
  [PackageDetailSessionEnum.All]: "All",
  [PackageDetailSessionEnum.Day]: "Day",
  [PackageDetailSessionEnum.Evening]: "Evening",
  [PackageDetailSessionEnum.Overnight]: "Overnight",
};

const SESSION_COUNT_LABELS = ["", "One", "Two", "Three", "Four", "Five"];

const BEAUTY_CODE_LABELS: Record<string, string> = {
  HBNCCMP: "Classic ManiPedi",
  HBNCGMP: "Gel ManiPedi",
  HBNCGMCP: "Gel Mani + Classic Pedi",
  HBNCCMA: "Classic Manicure",
  HBNCCPE: "Classic Pedicure",
  HBNCGMA: "Gel Manicure",
  HBNCGPE: "Gel Pedicure",
  HBNANA: "Nail Art (per nail)",
  HBNAFT: "French Tips",
  HBNAEX: "Nail Extension",
  HBNAGR: "Gel Removal",
  HBNAER: "Extension Removal",
  HBNAEM: "Extended Massage",
  HBNACT: "Callus Treatment",
  HBNDFGR: "Free Gel Removal",
};

const DEEP_CLEANING_LABELS: Record<string, { title: string; label: string }> = {
  DC1BR42C: { title: "501-750 SQFT", label: "Studio or 1 Bedroom, 2 Cleaners, 4 hours" },
  DC2BR43C: { title: "751-1000 SQFT", label: "Studio or 2 Bedroom, 3 Cleaners, 4 hours" },
  DC3BR43C: { title: "1001-1300 SQFT", label: "3 Bedroom, 3 Cleaners, 4 hours" },
  DC4BR44C: { title: "1301-1600 SQFT", label: "4 Bedroom, 4 Cleaners, 4 hours" },
  DC5BR45C: { title: "1601-2000 SQFT", label: "5 Bedroom, 5 Cleaners, 4 hours" },
  DC5BR46C: { title: "2001-2500 SQFT", label: "5 Bedroom, 6 Cleaners, 4 hours" },
  DCSBR42C: { title: "<500 SQFT", label: "Studio or 1 Bedroom, 2 Cleaners, 4 hours" },
};

function getUnitsLabel(department: PackageDepartmentEnum, serviceType: PackageServiceTypeEnum) {
  switch (department) {
    case PackageDepartmentEnum.ElderCare:
    case PackageDepartmentEnum.HomeCleaning:
      return "hr(s)";
    case PackageDepartmentEnum.CarpetUpholstery:
      return "item(s)";
    case PackageDepartmentEnum.Massage:
      return "session(s)";
    case PackageDepartmentEnum.HomeBeauty:
      return [
        PackageServiceTypeEnum.Manicure,
        PackageServiceTypeEnum.Pedicure,
        PackageServiceTypeEnum.Combo,
      ].includes(serviceType)
        ? "nail(s)"
        : "session(s)";
    default:
      return "unit(s)";
  }
}

function getServiceLabels(detail: PackageDetail) {
  const frequency = getFrequencyLabel(detail.repeatEvery, detail.repeatEveryTimes);

  if (
    detail.department === PackageDepartmentEnum.HomeCleaning ||
    detail.department === PackageDepartmentEnum.OfficeCleaning
  ) {
    return {
      serviceTitle: `${frequency} ${SERVICE_TYPE_LABELS[detail.serviceType]} (${SESSION_LABELS[detail.session]})`,
      serviceLabel: `${SESSION_COUNT_LABELS[detail.repeatEveryTimes] ?? ""} ${detail.duration}-Hour Session`,
    };
  }

  const isMassageAddOn =
    detail.department === PackageDepartmentEnum.Massage && detail.code.includes("AO");
  const serviceTitle = `${SERVICE_TYPE_LABELS[isMassageAddOn ? PackageServiceTypeEnum.AddOn : detail.serviceType]} (${frequency})`;

  if (detail.department === PackageDepartmentEnum.HomeBeauty) {
    return {
      serviceTitle: serviceTitle === "Discount (Ad Hoc)" ? "Free Item" : serviceTitle,
      serviceLabel: BEAUTY_CODE_LABELS[detail.code] ?? "-",
    };
  }

  if (detail.serviceType === PackageServiceTypeEnum.DeepCleaning) {
    const { title, label } = DEEP_CLEANING_LABELS[detail.code] ?? { title: "-", label: "-" };
    return { serviceTitle: title, serviceLabel: label };
  }

  return { serviceTitle, serviceLabel: UNIT_TYPE_LABELS[detail.unitType] };
}

function extractTextAfterColon(input: string) {
  const colonPosition = input.indexOf(":");
  return colonPosition !== -1 ? input.slice(colonPosition + 1).trim() : input;
}

function toLineItemDetail({
  packageDescription,
  billingValue,
  packageDetail,
}: PackageLineItem): LineItemDetail {
  const { serviceTitle, serviceLabel } = getServiceLabels(packageDetail);
  const isDeepCleaning = packageDetail.serviceType === PackageServiceTypeEnum.DeepCleaning;

  return {
    packageDetailId: packageDetail.id,
    serviceTitle,
    description:
      !isDeepCleaning && packageDescription
        ? extractTextAfterColon(packageDescription)
        : serviceLabel,
    department: packageDetail.department,
    serviceType: packageDetail.serviceType,
    unitsLabel: getUnitsLabel(packageDetail.department, packageDetail.serviceType),
    unitsNumber: packageDetail.units,
    billingValue,
  };
}

export function groupLineItems(lineItems: PackageLineItem[]) {
  const merged = new Map<string, LineItemDetail>();
  for (const item of lineItems.map(toLineItemDetail)) {
    const existing = merged.get(item.packageDetailId);
    merged.set(
      item.packageDetailId,
      existing
        ? {
            ...existing,
            unitsNumber: existing.unitsNumber + (item.unitsNumber || 0),
            billingValue: existing.billingValue + (item.billingValue || 0),
          }
        : item,
    );
  }

  const groups = new Map<string, LineItemDetail[]>();
  for (const item of merged.values()) {
    groups.set(item.serviceTitle, [...(groups.get(item.serviceTitle) ?? []), item]);
  }

  return [...groups.entries()];
}

export function formatLineItemAmount({
  serviceType,
  unitsNumber,
  unitsLabel,
  billingValue,
}: LineItemDetail) {
  const price = formatPrice(billingValue);
  return serviceType === PackageServiceTypeEnum.DeepCleaning
    ? price
    : `${unitsNumber || ""} ${unitsLabel} (${price})`;
}
